import { MaterialCommunityIcons } from '@expo/vector-icons';
import { ILogger, normalizeUrl, useGrippContext } from '@gripp/shared-logic';
import { useCallback } from 'react';
import { Linking, Pressable, StyleSheet } from 'react-native';
import { Card } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { XIcon } from '../crop-circle/icons';
import { colors } from '../crop-circle/tokens';
import { LinkData, MediaComponent, MediaComponentProps } from './types';

export const LinkComponent: MediaComponent<LinkData> = ({
  displayMode = 'card',
  displayWidth,
  onPress,
  canDelete,
  ...props
}: MediaComponentProps<LinkData>) => {
  const { logger } = useGrippContext();
  const linkCardStyle =
    displayMode === 'card' ? styles.linkThumbCard : styles.linkOutlinedCard;
  const defaultSize = 88;
  const displaySize = displayWidth ?? defaultSize;

  const handlePress = useCallback(() => {
    if (onPress) {
      onPress();
      return;
    }
    openLink(props.mediaItem.url, logger);
  }, [props.mediaItem.url, logger, onPress]);

  return (
    <Card
      onPress={handlePress}
      mode={displayMode === 'outlined' ? 'outlined' : 'elevated'}
      style={[
        styles.baseCardCropCircle,
        linkCardStyle,
        { width: displaySize, height: displaySize },
      ]}
    >
      {canDelete && (
        <Pressable
          style={styles.closeButton}
          onPress={() => props.deleteMediaItem?.(props.mediaItem.id)}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Remove link"
        >
          <XIcon size={8} color={Colors.role.textStrong} />
        </Pressable>
      )}
      <Card.Content style={styles.cardContent}>
        <MaterialCommunityIcons
          name={'link'}
          color={Colors.primaryDark}
          size={displaySize * 0.4}
        />
      </Card.Content>
    </Card>
  );
};

const openLink = async (url: string, logger?: ILogger) => {
  try {
    const normalizedUrl = normalizeUrl(url);
    await Linking.openURL(normalizedUrl);
  } catch (error) {
    logger?.error(error, `Failed to open link: ${url}`);
  }
};

const styles = StyleSheet.create({
  baseCardCropCircle: {
    borderRadius: 4,
    backgroundColor: Colors.role.surfaceRaised,
    cursor: 'pointer',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardContent: {
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    padding: 0,
    margin: 0,
  },
  linkThumbCard: {},
  linkOutlinedCard: {
    borderWidth: 0.5,
    borderColor: Colors.role.borderStrong,
  },
  closeButton: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 16,
    height: 16,
    borderRadius: 99,
    backgroundColor: Colors.role.surfaceInverseSoftHover,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
  },
});
